import React, { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Check, ChevronDown, ChevronRight, Search } from "lucide-react";
import { popoverStyle, useFloatingPopover } from "./useFloatingPopover";
import "./AutoSourcing.css";

/**
 * Shared Liquid Glass single-select combobox (Retailer, Category, Advanced
 * selects). The menu matches the trigger width, keeps the search row sticky,
 * and marks the one selected option with a green check. It renders in a
 * portal and opens below the trigger, flipping above when there is not
 * enough room below (see useFloatingPopover).
 *
 * options: [{ key, label, description?, leading?, trailing?, disabled?,
 *             selected?, separated?, before?, title?, onSelect,
 *             keepOpen?, expandable? }]
 *
 * Drill-down menus: ``keepOpen`` options run ``onSelect`` without closing,
 * ``expandable`` options show a chevron and open with ArrowRight, ``onBack``
 * runs on ArrowLeft/Backspace with an empty search, ``header`` renders under
 * the search row, and a new ``resetKey`` moves the highlight to the top.
 */
export default function GlassCombobox({
  id,
  label = null,
  placeholder = "Select",
  leading = null,
  options = [],
  disabled = false,
  searchable = true,
  query = "",
  onQueryChange,
  searchPlaceholder = "Search...",
  searchLabel = "Search",
  listLabel = "Options",
  status = null,
  footer = null,
  maxHeight = 380,
  ariaLabel,
  className = "",
  triggerClassName = "",
  testId = "glass-combobox",
  onOpenChange,
  header = null,
  onBack,
  resetKey,
}) {
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const [menuWidth, setMenuWidth] = useState(null);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const searchRef = useRef(null);
  const listRef = useRef(null);
  const menuRef = useRef(null);
  const listboxId = `${useId()}-listbox`;

  const setOpenState = (next) => {
    setOpen(next);
    onOpenChange?.(next);
  };

  const openMenu = () => {
    if (disabled) return;
    onQueryChange?.("");
    const selectedIndex = options.findIndex((o) => o.selected);
    setHighlight(selectedIndex >= 0 ? selectedIndex : 0);
    const width = triggerRef.current?.getBoundingClientRect?.().width;
    setMenuWidth(width > 0 ? Math.round(width) : null);
    setOpenState(true);
  };

  const closeMenu = ({ focusTrigger = true } = {}) => {
    setOpenState(false);
    onQueryChange?.("");
    if (focusTrigger) triggerRef.current?.focus();
  };

  const choose = (opt) => {
    if (!opt || opt.disabled) return;
    opt.onSelect?.();
    if (!opt.keepOpen) closeMenu();
  };

  useEffect(() => {
    if (!open) return;
    const selectedIndex = options.findIndex((o) => o.selected);
    setHighlight(selectedIndex >= 0 ? selectedIndex : 0);
    (searchRef.current || listRef.current)?.focus?.();
    if (listRef.current) listRef.current.scrollTop = 0;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  useEffect(() => {
    if (!open) return undefined;
    (searchRef.current || listRef.current)?.focus?.();
    const selected = listRef.current?.querySelector('[aria-selected="true"]');
    if (selected && typeof selected.scrollIntoView === "function") {
      selected.scrollIntoView({ block: "nearest" });
    }
    function onPointerDown(event) {
      if (menuRef.current?.contains(event.target)) return;
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        closeMenu({ focusTrigger: false });
      }
    }
    function onDocKeyDown(event) {
      if (event.key === "Escape") closeMenu();
    }
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onDocKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onDocKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const el = listRef.current?.querySelector(`[data-index="${highlight}"]`);
    if (el && typeof el.scrollIntoView === "function") el.scrollIntoView({ block: "nearest" });
  }, [highlight, open]);

  const onMenuKeyDown = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      closeMenu();
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, options.length - 1));
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
      return;
    }
    if (e.key === "Home") {
      e.preventDefault();
      setHighlight(0);
      return;
    }
    if (e.key === "End") {
      e.preventDefault();
      setHighlight(Math.max(options.length - 1, 0));
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      choose(options[highlight]);
      return;
    }
    const emptyQuery = !searchable || !query;
    if (e.key === "ArrowRight" && emptyQuery && options[highlight]?.expandable) {
      e.preventDefault();
      choose(options[highlight]);
      return;
    }
    if ((e.key === "ArrowLeft" || e.key === "Backspace") && emptyQuery && onBack) {
      e.preventDefault();
      onBack();
    }
  };

  const onTriggerKeyDown = (e) => {
    if (!open && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
      e.preventDefault();
      openMenu();
    }
  };

  const activeOptionId = open && options[highlight] ? `${listboxId}-opt-${highlight}` : undefined;
  const position = useFloatingPopover({
    open,
    anchorRef: triggerRef,
    floatingRef: menuRef,
    width: menuWidth || 320,
    measure: (el) => {
      const scroll = el.querySelector(".as-glass-scroll");
      const foot = el.querySelector(".as-glass-foot");
      return Math.min(scroll?.scrollHeight || 0, maxHeight) + (foot?.offsetHeight || 0) + 2;
    },
  });

  return (
    <div className={`as-glass-root ${className}`} ref={rootRef} data-testid={testId}>
      <button
        ref={triggerRef}
        id={id}
        type="button"
        role="combobox"
        className={`as-glass-trigger ${triggerClassName} ${open ? "open" : ""}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={() => (open ? closeMenu() : openMenu())}
        onKeyDown={onTriggerKeyDown}
        data-testid={`${testId}-trigger`}
      >
        {leading ? <span className="as-glass-trigger-leading">{leading}</span> : null}
        <span className={`as-glass-trigger-body ${label ? "" : "placeholder"}`}>{label || placeholder}</span>
        <ChevronDown className="as-glass-chevron" aria-hidden="true" />
      </button>

      {open && createPortal(
        <div
          ref={menuRef}
          className={`as-glass-menu as-popover placement-${position?.placement || "bottom"}`}
          style={popoverStyle(position)}
          data-placement={position?.placement || "bottom"}
          data-testid={`${testId}-menu`}
          onKeyDown={onMenuKeyDown}
        >
          <div
            ref={listRef}
            className="as-glass-scroll"
            style={{ "--as-glass-max": `${maxHeight}px` }}
            tabIndex={searchable ? undefined : -1}
            data-testid={`${testId}-scroll`}
          >
            {searchable && (
              <div className="as-glass-search">
                <div className="as-glass-search-field">
                  <Search className="as-glass-search-icon" aria-hidden="true" />
                  <input
                    ref={searchRef}
                    value={query}
                    onChange={(e) => {
                      onQueryChange?.(e.target.value);
                      setHighlight(0);
                    }}
                    placeholder={searchPlaceholder}
                    aria-label={searchLabel}
                    aria-controls={listboxId}
                    aria-activedescendant={activeOptionId}
                    data-testid={`${testId}-search`}
                  />
                </div>
                {header}
              </div>
            )}
            <div role="listbox" id={listboxId} aria-label={listLabel} aria-activedescendant={searchable ? undefined : activeOptionId}>
              {status}
              {options.map((opt, index) => (
                <React.Fragment key={opt.key}>
                  {opt.separated && <div className="as-glass-separator" role="separator" />}
                  {opt.before}
                  <div
                    id={`${listboxId}-opt-${index}`}
                    role="option"
                    aria-selected={Boolean(opt.selected)}
                    aria-disabled={opt.disabled ? true : undefined}
                    data-index={index}
                    title={opt.title}
                    className={[
                      "as-glass-option",
                      opt.selected ? "selected" : "",
                      opt.disabled ? "disabled" : "",
                      index === highlight ? "highlighted" : "",
                    ].join(" ")}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => choose(opt)}
                    onMouseEnter={() => setHighlight(index)}
                  >
                    {opt.leading}
                    <span className="as-glass-option-copy">
                      <span className="as-glass-option-label">{opt.label}</span>
                      {opt.description ? <small>{opt.description}</small> : null}
                    </span>
                    {opt.trailing}
                    {opt.expandable ? (
                      <ChevronRight className="as-glass-drill" aria-hidden="true" data-testid={`${testId}-drill`} />
                    ) : null}
                    {opt.selected ? (
                      <Check className="as-glass-check" aria-hidden="true" data-testid={`${testId}-check`} />
                    ) : null}
                  </div>
                </React.Fragment>
              ))}
            </div>
          </div>
          {footer ? <div className="as-glass-foot">{footer}</div> : null}
        </div>,
        document.body,
      )}
    </div>
  );
}
